import { useState, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { useHousehold } from '@contexts/HouseholdContext';
import { getApiBaseMode, setApiBaseMode } from '@lib/apiBase';

/**
 * Gates render until a household is selected.
 *
 * Three terminal states:
 *   - Loading: blank spinner (single network round-trip on mount)
 *   - No households: "Create your first household" inline form. Nothing
 *     else renders behind it because every protected API call would
 *     403 without an X-Household-Id, so we don't even attempt fetches.
 *   - Active household chosen: render children normally.
 *
 * Sits inside HouseholdProvider, outside the dashboard tree.
 */
export default function HouseholdGate({ children }: { children: ReactNode }) {
  const { ready, households, activeId, createHousehold, fetchError, refresh } = useHousehold();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-700 border-t-emerald-400" />
      </div>
    );
  }

  // Fetch failed and we have no cached list — show the real error +
  // recovery options. Do NOT fall through to the create-household form;
  // that misleadingly suggests "you have no household" when actually
  // we couldn't load the list. Common case: just switched to tailnet
  // mode and the tailnet API is unreachable from this device.
  if (fetchError && households.length === 0) {
    const onTailnet = getApiBaseMode() === 'tailnet';
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-6">
        <div className="w-full max-w-md rounded-lg border border-zinc-800 bg-zinc-900 p-6 shadow-xl">
          <h2 className="mb-1 text-xl font-semibold text-zinc-100">Couldn't load your households</h2>
          <p className="mb-2 text-sm text-amber-400 font-mono break-words">
            {fetchError.message}
          </p>
          <p className="mb-4 text-sm text-zinc-400">
            {onTailnet
              ? 'You\'re in Tailnet mode. If your device can\'t reach the tailnet host (Tailscale off, browser DNS bypass, etc.), API calls fail. Try switching back to Public.'
              : 'Network or auth error. Retry, or sign out and back in.'}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { void refresh(); }}
              className="flex-1 rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm font-medium text-zinc-100 hover:bg-zinc-700"
            >
              Retry
            </button>
            {onTailnet && (
              <button
                type="button"
                onClick={() => setApiBaseMode('public')}
                className="flex-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500"
              >
                Switch to Public
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (households.length === 0) {
    const submit = async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) return;
      setSubmitting(true);
      const res = await createHousehold(trimmed);
      setSubmitting(false);
      if (res.ok) {
        toast.success('Household created');
        // No reload needed — context state updates trigger dashboard render.
      } else {
        toast.error(res.error);
      }
    };
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-6">
        <form
          onSubmit={submit}
          className="w-full max-w-md rounded-lg border border-zinc-800 bg-zinc-900 p-6 shadow-xl"
        >
          <h2 className="mb-1 text-xl font-semibold text-zinc-100">Welcome to StreamFlow</h2>
          <p className="mb-4 text-sm text-zinc-400">
            Households group your providers, family members, and devices.
            Pick a name to get started — you can rename or invite people later.
          </p>
          <input
            type="text"
            placeholder="The Smith Household"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mb-3 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500 focus:outline-none"
            autoFocus
          />
          <button
            type="submit"
            disabled={submitting || !name.trim()}
            className="w-full rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create household'}
          </button>
        </form>
      </div>
    );
  }

  // Edge case: ready, has households, but activeId is null somehow.
  // Don't render the protected tree (it would fire X-Household-Id-less
  // requests that 403). The user can pick from the switcher in the
  // header — but the header itself only renders inside the protected
  // tree, so prompt them here instead.
  if (!activeId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950 text-zinc-300">
        Select a household from the switcher to continue.
      </div>
    );
  }

  return <>{children}</>;
}
